import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import { templatesAPI } from '../../services/api';
import VariablePicker from './VariablePicker';

const CHANNELS = ['EMAIL', 'WHATSAPP'];
const MODULES = ['CRM', 'ATS', 'INVOICE'];

const CreateTemplateModal = ({ isOpen, onClose, onSaved, editTemplate, defaults }) => {
  const [name, setName] = useState('');
  const [channel, setChannel] = useState('EMAIL');
  const [module, setModule] = useState('CRM');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  const isEdit = Boolean(editTemplate);

  useEffect(() => {
    if (!isOpen) return;
    if (editTemplate) {
      setName(editTemplate.name || '');
      setChannel(editTemplate.channel || 'EMAIL');
      setModule(editTemplate.module || 'CRM');
      setSubject(editTemplate.subject || '');
      setBody(editTemplate.body || '');
    } else {
      setName('');
      setChannel(defaults?.channel || 'EMAIL');
      setModule(defaults?.module || 'CRM');
      setSubject('');
      setBody('');
    }
  }, [isOpen, editTemplate, defaults]);

  const insertVariable = (variable) => {
    const textarea = bodyRef.current;
    if (!textarea) { setBody((b) => b + variable); return; }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newBody = body.slice(0, start) + variable + body.slice(end);
    setBody(newBody);
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + variable.length;
      textarea.focus();
    }, 0);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Template name is required'); return; }
    if (!body.trim()) { toast.error('Template body is required'); return; }
    if (channel === 'EMAIL' && !subject.trim()) { toast.error('Subject is required for email templates'); return; }
    if (channel === 'WHATSAPP' && body.length > 1000) { toast.error('WhatsApp body must be 1000 characters or less'); return; }

    setSaving(true);
    try {
      const payload = { name: name.trim(), channel, module, subject: subject.trim(), body: body.trim() };
      if (isEdit) {
        await templatesAPI.updateTemplate(editTemplate.id, payload);
        toast.success('Template updated');
      } else {
        await templatesAPI.createTemplate(payload);
        toast.success('Template created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || (isEdit ? 'Failed to update template' : 'Failed to create template'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const charCount = body.length;
  const isWhatsApp = channel === 'WHATSAPP';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <h2 className="text-base font-bold text-slate-900">
            {isEdit ? 'Edit Template' : 'New Template'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Template Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Interview Confirmation"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Channel + Module */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Channel
              </label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                disabled={isEdit}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white disabled:bg-slate-50 disabled:text-slate-400"
              >
                {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Module
              </label>
              <select
                value={module}
                onChange={(e) => setModule(e.target.value)}
                disabled={isEdit}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white disabled:bg-slate-50 disabled:text-slate-400"
              >
                {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          {/* Subject (EMAIL only) */}
          {!isWhatsApp && (
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject…"
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          )}

          {/* Body */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Body <span className="text-red-500">*</span>
            </label>
            <textarea
              ref={bodyRef}
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={isWhatsApp ? 1000 : undefined}
              placeholder="Write your template body here… Use {variableName} for dynamic values."
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
            />
            {isWhatsApp && (
              <p className={`text-right text-xs mt-1 ${charCount >= 900 ? 'text-red-500 font-semibold' : 'text-slate-400'}`}>
                {charCount}/1000
              </p>
            )}
          </div>

          {/* Variable Picker */}
          <VariablePicker module={module} onInsert={insertVariable} />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold disabled:opacity-60 transition-colors"
          >
            {saving ? 'Saving…' : isEdit ? 'Update Template' : 'Create Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplateModal;
